import React, { useState, useEffect, useMemo } from "react";
import {
  IconCheck,
  IconCurrencyDollar,
  IconChevronRight,
  IconLoader,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import {
  useFetchCodOrdersTransactionsData,
  useDRSShipmentPaymentCollectMutation,
} from "../../../apis/drsApi";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import NoDataFound from "../../../components/common/NoDataFound";
import Pagination from "../../../components/common/Pagination";
import EntriesSelector from "../../../components/common/EntriesSelector";
import NormalSearch from "../../../components/common/NormalSearch";
import useDebounce from "../../../utilities/useDebounce";
import toast from "react-hot-toast";
import formatDate, {
  formatDateTime,
  formatDateTimeNew,
  formatDateWithTime,
} from "../../../utilities/helper";
import { usePermission } from "../../../hooks/usePermissionTabs";
import { useLanguage } from "../../../context/LanguageContext";

interface CODSettlementViewProps {
  drsList?: any[];
  onStatsUpdate?: (stats: {
    ready: number;
    pendingCod: number;
    exposure: number;
    openCases: number;
  }) => void;
}

interface CODSettlementDeskProps {
  selectedDrs: any;
  refetchList: () => void;
  onClose: () => void;
}

const CODSettlementDesk: React.FC<CODSettlementDeskProps> = ({
  selectedDrs,
  refetchList,
  onClose,
}) => {
  const { t } = useLanguage();
  const { allowed: canUpdateDrs } = usePermission("drs", "update");
  const [partialAmount, setPartialAmount] = useState<string>("");

  const [showCardModal, setShowCardModal] = useState(false);

  const cardTransactions = selectedDrs?.cardTransactions || [
    { id: 1, orderId: "ORD-001", amount: 45.5, date: "2025-03-15 10:30" },
    { id: 2, orderId: "ORD-002", amount: 120.0, date: "2025-03-15 11:15" },
  ];

  // Use payment collect mutation bound to the selected DRS UUID
  const { mutate: collectPayment, isPending } =
    useDRSShipmentPaymentCollectMutation(selectedDrs?.id);

  const handleCollect = (amountToCollect: number | string) => {
    if (!canUpdateDrs) {
      toast.error("You do not have permission to collect COD");
      return;
    }
    if (!selectedDrs) return;
    const parsedAmount = parseFloat(amountToCollect.toString());
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    collectPayment(
      { amount: parsedAmount },
      {
        onSuccess: () => {
          toast.success(`Successfully collected SAR ${parsedAmount}`);
          setPartialAmount("");
          refetchList();
        },
        onError: (error: any) => {
          console.error(error);
          const errMsg =
            error?.response?.data?.message ||
            error?.message ||
            "Failed to collect payment";
          toast.error(errMsg);
        },
      },
    );
  };

  const pendingAmountVal = parseFloat(selectedDrs?.pendingAmount || 0);
  const driverWalletVal = parseFloat(selectedDrs?.driverWallet || 0);

  return (
    <>
      <div className="bg-white/20 rounded-[24px] border border-[#E5E5E5]  shadow-sm text-[#212121] flex flex-col relative overflow-hidden h-full min-h-[520px]">
        {/* Header */}
        <div className="flex items-center justify-between relative z-10  border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-[#212121] w-full">
              {t("DRS Settlement")}{" "}
              {selectedDrs?.drsId && (
                <span className="text-green-600">#{selectedDrs.drsId}</span>
              )}
            </h2>
            {selectedDrs && (
              <p className="text-xs text-gray-500 font-semibold">
                {selectedDrs.driver || "N/A"} &bull;{" "}
                {selectedDrs.routeCode || "No Route"}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={
                isPending ||
                pendingAmountVal <= 0 ||
                selectedDrs?.status === "CLOSED" ||
                driverWalletVal < pendingAmountVal
              }
              onClick={() => handleCollect(pendingAmountVal)}
              className="w-full bg-[#00A859] hover:bg-green-600 text-white p-2 rounded-lg text-xs font-bold flex justify-center items-center gap-2 transition-all active:scale-[0.98] shadow-[0_4px_12px_rgba(0,168,89,0.25)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending ? (
                <IconLoader size={18} className="animate-spin" />
              ) : (
                <IconCheck size={18} />
              )}
              {t("Settle Full COD")}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg border border-[#D2D2D2]"
              title={t("Close")}
            >
              <IconX size={18} stroke={2.5} />
            </button>
          </div>
        </div>

        {/* DRS Details & Metrics Section */}
        <div className="space-y-2 p-4 relative z-10">
          {/* Section 1: Order & Shipment Summary */}
          <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
            <div className="flex items-center justify-between mb-3 border-b border-[#E9ECEF] pb-2">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-3.5 bg-[#FBD037] rounded-full"></div>
                <span className="text-[11px] font-bold text-gray-700 tracking-wider uppercase">
                  {t("Shipment & Order Summary")}
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] mr-1">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                <span className="text-gray-500 font-bold">{t("COD Shipments:")}</span>
                <span className="text-[#212121] font-black">
                  {selectedDrs?.totalCodOrders || 0}
                </span>
              </div>
            </div>

            {/* Orders Statuses (5 Columns) */}
            <div className="grid grid-cols-5 gap-1 text-center bg-white border border-[#E9ECEF] rounded-xl p-2 mb-1">
              <div>
                <p className="text-[9px] text-gray-400 font-bold uppercase mb-0.5">
                  {t("Total")}
                </p>
                <p className="text-sm font-black text-[#212121]">
                  {selectedDrs?.total_shipments || 0}
                </p>
              </div>
              <div>
                <p className="text-[9px] text-amber-500 font-bold uppercase mb-0.5">
                  {t("OFD")}
                </p>
                <p className="text-sm font-black text-amber-600">
                  {selectedDrs?.outForDelivery || 0}
                </p>
              </div>
              <div>
                <p className="text-[9px] text-emerald-500 font-bold uppercase mb-0.5">
                  {t("POD")}
                </p>
                <p className="text-sm font-black text-emerald-600">
                  {selectedDrs?.ordersDelivered || 0}
                </p>
              </div>
              <div>
                <p className="text-[9px] text-rose-500 font-bold uppercase mb-0.5">
                  {t("ND")}
                </p>
                <p className="text-sm font-black text-rose-500">
                  {selectedDrs?.ordersNotDelivered || 0}
                </p>
              </div>
              <div>
                <p className="text-[9px] text-purple-500 font-bold uppercase mb-0.5">
                  {t("RTW")}
                </p>
                <p className="text-sm font-black text-purple-600">
                  {selectedDrs?.receiveInWarehouse || 0}
                </p>
              </div>
            </div>
          </div>

          {/* Section 2: Driver Financials */}
          <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
            <div className="flex items-center gap-2 mb-3 border-b border-[#E9ECEF] pb-2">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
              <div className="w-1.5 h-3.5 bg-[#00A859] rounded-full"></div>
              <span className="text-[11px] font-bold text-gray-700 tracking-wider uppercase">
                {t("Driver Financials")}
              </span>
            </div>

            <div className="space-y-3">
              {/* Driver Collection (Card/Cash) */}
              <div className="grid grid-cols-2 gap-3">
                <div
                  className="bg-white border border-[#E9ECEF] rounded-xl p-2 flex items-center justify-between"
                  onClick={() => setShowCardModal(true)}
                >
                  <span className="text-[9px] font-bold text-gray-400 uppercase block">
                    {t("Digital (Card)")}
                  </span>
                  <span className="text-sm font-black text-green-600 flex items-center gap-1">
                    <img src="/img/riyal-icon.svg" alt="" className="w-3 h-3" />{" "}
                    {selectedDrs?.cardAmount || 0}
                  </span>
                </div>
                <div className="bg-white border border-[#E9ECEF] rounded-xl p-2 flex items-center justify-between">
                  <span className="text-[9px] font-bold text-gray-400 uppercase block">
                    {t("Cash")}
                  </span>
                  <span className="text-sm font-black text-green-600 flex items-center gap-1">
                    <img src="/img/riyal-icon.svg" alt="" className="w-3 h-3" />{" "}
                    {selectedDrs?.codAmount || 0}
                  </span>
                </div>
                <div className="bg-white border border-[#E9ECEF] rounded-xl p-2 flex items-center justify-between">
                  <span className="text-[9px] font-bold text-gray-400 uppercase block">
                    {t("Collected")}
                  </span>
                  <span className="text-sm font-black text-green-600 flex items-center gap-1">
                    <img src="/img/riyal-icon.svg" alt="" className="w-3 h-3" />{" "}
                    {selectedDrs?.collectedAmount || 0}
                  </span>
                </div>
                <div className="bg-white border border-[#E9ECEF] rounded-xl p-2 flex items-center justify-between">
                  <span className="text-[9px] font-bold text-gray-400 uppercase block">
                    {t("Pending")}
                  </span>
                  <span className="text-sm font-black text-red-500 flex items-center gap-1">
                    <img src="/img/riyal-icon.svg" alt="" className="w-3 h-3" />{" "}
                    {selectedDrs?.pendingAmount || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Settlement History */}
          {selectedDrs?.settlementHistory &&
            selectedDrs.settlementHistory.length > 0 && (
              <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                <div className="flex items-center gap-2 mb-2 border-b border-[#E9ECEF] pb-1.5">
                  <div className="w-1.5 h-3.5 bg-blue-500 rounded-full"></div>
                  <span className="text-[11px] font-bold text-gray-700 tracking-wider uppercase">
                    {t("Collection History")}
                  </span>
                </div>
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
                <div className="space-y-2 pr-2 h-[100px] overflow-hidden overflow-y-auto">
                  {selectedDrs.settlementHistory.map((hist: any) => (
                    <div
                      key={hist.id}
                      className="bg-white border border-[#E9ECEF] rounded-xl p-3.5 flex items-center justify-between shadow-sm hover:border-blue-100 transition-colors"
                    >
                      <div className="space-y-0.5">
                        <span className="text-[10px] font-bold text-gray-400 block uppercase">
                          {t("Collected By")}
                        </span>
                        <span className="text-xs font-medium text-[#212121]">
                          {hist.collectedByName || "Accountant"}
                        </span>
                        {/* <span className="text-[9px] text-gray-400 block font-medium"> 
                        {formatDateTimeNew(hist.createdAt)}
                      </span> */}
                      </div>
                      <div className="text-center">
                        <span className="text-[10px] font-bold text-gray-400 block uppercase">
                          {t("Date")}
                        </span>
                        <span className="text-xs font-medium text-[#212121]">
                          {/* {new Date(hist.createdAt).toLocaleString()} */}
                          {formatDateTimeNew(hist.createdAt)}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-[10px] font-bold text-gray-400 block uppercase">
                          {t("Amount")}
                        </span>
                        <span className="text-xs font-medium text-green-600 flex items-center gap-1">
                          <img
                            src="/img/riyal-icon.svg"
                            alt=""
                            className="w-3 h-3"
                          />{" "}
                          {parseFloat(hist.amount).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          <div className="group  rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
            {/*  & Wallet stats */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-sm font-bold text-[#212121]">
                  {t("Pending COD:")}
                </span>
                <span className="text-lg font-black text-red-500 flex items-center gap-1">
                  <img src="/img/riyal-icon.svg" alt="" className="w-3 h-3" />{" "}
                  {pendingAmountVal}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-500 font-medium">
                  {t("Current Driver Wallet:")}
                </span>
                <span className="text-blue-600 font-bold flex items-center gap-1">
                  <img src="/img/riyal-icon.svg" alt="" className="w-3 h-3" />{" "}
                  {driverWalletVal}
                </span>
              </div>
            </div>
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

            {/* Action input and buttons */}
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="number"
                  step="any"
                  value={partialAmount}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    const maxAllowed = Math.min(
                      pendingAmountVal,
                      driverWalletVal,
                    );
                    if (val > maxAllowed) {
                      setPartialAmount(maxAllowed.toString());
                    } else {
                      setPartialAmount(e.target.value);
                    }
                  }}
                  placeholder={
                    driverWalletVal <= 0
                      ? t("No COD collected by driver")
                      : t("Enter amount to collect")
                  }
                  disabled={
                    isPending || pendingAmountVal <= 0 || driverWalletVal <= 0
                  }
                  className="flex-1 bg-white border border-[#E5E5E5] rounded-xl px-4 py-2.5 text-sm text-[#212121] placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors disabled:bg-gray-100 disabled:text-gray-400 font-semibold"
                />
                <button
                  disabled={
                    isPending ||
                    pendingAmountVal <= 0 ||
                    driverWalletVal <= 0 ||
                    !partialAmount ||
                    parseFloat(partialAmount) <= 0 ||
                    parseFloat(partialAmount) > driverWalletVal
                  }
                  onClick={() => handleCollect(partialAmount)}
                  className="bg-[#FBD037] hover:bg-[#eab308] text-[#212121] px-5 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 active:scale-[0.98]"
                >
                  {isPending && (
                    <IconLoader size={14} className="animate-spin" />
                  )}
                  {t("Collect")}
                </button>
              </div>

              {driverWalletVal <= 0 && pendingAmountVal > 0 && (
                <p className="text-[10px] text-red-500 font-semibold text-center bg-red-50 border border-red-100 rounded-lg p-2">
                  * {t("Driver has not collected any COD yet.")}
                </p>
              )}

              {driverWalletVal < pendingAmountVal &&
                pendingAmountVal > 0 &&
                driverWalletVal > 0 && (
                  <p className="text-[10px] text-red-500 font-semibold bg-red-50 border border-red-100 rounded-lg p-2 text-center">
                    * {t("Driver wallet balance is less than COD.")}
                  </p>
                )}
            </div>
          </div>
        </div>

        {/* Settle Actions Section */}
      </div>
      {showCardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-bold text-[#212121]">
                {t("Card Payment Details")}
              </h3>
              <button
                onClick={() => setShowCardModal(false)}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <IconX size={20} stroke={2} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
                <span className="text-sm font-medium text-gray-500">
                  {t("Total Card Amount")}
                </span>
                <span className="text-xl font-bold text-green-600 flex items-center gap-1">
                  <img src="/img/riyal-icon.svg" alt="" className="w-4 h-4" />
                  {selectedDrs?.cardAmount || 0}
                </span>
              </div>

              {cardTransactions.length > 0 ? (
                <ul className="space-y-3">
                  {cardTransactions.map((tx: any) => (
                    <li
                      key={tx.id}
                      className="bg-gray-50 rounded-lg p-3 flex justify-between items-center"
                    >
                      <div>
                        <p className="text-sm font-medium text-[#212121]">
                          {tx.orderId || "Order"}
                        </p>
                        <p className="text-xs text-gray-400">
                          {tx.date || "N/A"}
                        </p>
                      </div>
                      <span className="text-sm font-bold text-green-600 flex items-center gap-1">
                        <img
                          src="/img/riyal-icon.svg"
                          alt=""
                          className="w-3 h-3"
                        />
                        {tx.amount?.toFixed(2) || "0.00"}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-center text-gray-500 py-6">
                  {t("No card transaction details available.")}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const CODSettlementView: React.FC<CODSettlementViewProps> = ({
  drsList,
  onStatsUpdate,
}) => {
  const { t } = useLanguage();
  const [selectedDrs, setSelectedDrs] = useState<any | null>(null);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  // const [showFlow, setShowFlow] = useState<boolean>(false);

  // Real API hooks with Search, Pagination & Limit
  const [searchFor, setSearchFor] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const debouncedSearchFor = useDebounce(searchFor, 550);

  const {
    data: codOrders,
    isPending: listPending,
    isFetching,
    refetch,
  } = useFetchCodOrdersTransactionsData(pageNo, limit, debouncedSearchFor);

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  useEffect(() => {
    refetch();
  }, [debouncedSearchFor, pageNo, limit]);

  // Filter out any DRS records that only contain CC shipments (0 COD shipments)
  const activeCodDrsList = useMemo(() => {
    return (
      codOrders?.drs?.filter(
        (item: any) => parseInt(item.totalCodOrders || 0) > 0,
      ) || []
    );
  }, [codOrders?.drs]);

  // Keep selectedDrs object in sync with list data (updates  balances, etc.)
  // Only sync if a DRS was already explicitly selected by the user — do NOT auto-select first item.
  useEffect(() => {
    if (activeCodDrsList.length && selectedDrs) {
      const updated = activeCodDrsList.find(
        (item: any) => item.id === selectedDrs.id,
      );
      if (updated) {
        setSelectedDrs(updated);
      }
    }
  }, [activeCodDrsList]);

  // Calculations for stats based on the filtered list
  const eligibleDRSCount =
    activeCodDrsList.filter((item: any) => {
      const isEligible =
        parseInt(item.ordersNotDelivered || 0) ===
          parseInt(item.receiveInWarehouse || 0) &&
        parseInt(item.outForDelivery || 0) === 0;
      return isEligible && item.status !== "CLOSED";
    }).length || 0;

  const totalPendingCod =
    activeCodDrsList.reduce(
      (acc: number, item: any) => acc + parseFloat(item.pendingAmount || 0),
      0,
    ) || 0;
  const totalWalletExposure = (() => {
    const seenDrivers = new Set<string>();
    return (
      activeCodDrsList.reduce((acc: number, item: any) => {
        const driverIdentifier = item.driverCode || item.driver || "";
        if (driverIdentifier && !seenDrivers.has(driverIdentifier)) {
          seenDrivers.add(driverIdentifier);
          return acc + parseFloat(item.driverWallet || 0);
        }
        return acc;
      }, 0) || 0
    );
  })();

  const getReconciliationCheck = (item: any) => {
    const isEligible =
      parseInt(item.ordersNotDelivered || 0) ===
        parseInt(item.receiveInWarehouse || 0) &&
      parseInt(item.outForDelivery || 0) === 0;
    return isEligible ? "ELIGIBLE (RIW OK)" : "PENDING SHIPMENTS";
  };

  const getCodStatus = (item: any) => {
    const totalCodOrders = parseInt(item.totalCodOrders || 0);
    const pendingAmount = parseFloat(item.pendingAmount || 0);

    // 1. If all shipments are CC (no COD orders)
    if (totalCodOrders === 0) {
      return "Closed";
    }

    // 2. If it has COD shipments, check if pending  COD is 0
    if (pendingAmount === 0) {
      return "Collected";
    }

    // 3. Otherwise, it is Pending
    return "Pending";
  };

  useEffect(() => {
    if (onStatsUpdate) {
      onStatsUpdate({
        ready: eligibleDRSCount,
        pendingCod: totalPendingCod,
        exposure: totalWalletExposure,
        openCases: codOrders?.total || 0,
      });
    }
  }, [
    eligibleDRSCount,
    totalPendingCod,
    totalWalletExposure,
    codOrders?.total,
    onStatsUpdate,
  ]);

  return (
    <div className="grid grid-cols-12 gap-6 p-2  bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-ful rounded-r-3xl rounded-bl-3xl w-full">
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
      <div
        className={selectedDrs ? "col-span-12 xl:col-span-7" : "col-span-12"}
      >
        <div className="bg-white rounded-3xl border overflow-visible">
          <div className="px-6 py-1.5 flex flex-col md:flex-row md:items-center justify-between gap-4 select-none">
            <div className="flex flex-wrap items-center gap-3"></div>

            <div className="relative flex items-center">
              <div
                onClick={() => {
                  if (!showSearch) setShowSearch(true);
                }}
                className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-md bg-white shadow-sm h-8 shrink-0 ${
                  showSearch
                    ? "w-64 px-2 cursor-default"
                    : "w-8 justify-center cursor-pointer"
                }`}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSearch(!showSearch);
                  }}
                  className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                  title={t("Search")}
                >
                  <IconSearch size={17} stroke={2.5} />
                </button>
                {showSearch && (
                  <>
                    <input
                      type="text"
                      value={searchFor}
                      onChange={(e) => {
                        setSearchFor(e.target.value);
                        setPageNo(1);
                      }}
                      placeholder={t("Search...")}
                      autoFocus
                      className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none border-0 focus:ring-0 focus:outline-none p-0"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchFor("");
                        setShowSearch(false);
                        setPageNo(1);
                      }}
                      className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                      title={t("Clear & Close")}
                    >
                      <IconX size={14} stroke={2.5} />
                    </button>
                  </>
                )}
              </div>

              {codOrders && codOrders.totalPages * limit > 10 && (
                <EntriesSelector
                  value={limit}
                  options={[10, 20, 50, 100]}
                  onChange={(val) => {
                    setLimit(val);
                    setPageNo(1);
                  }}
                />
              )}
            </div>
          </div>

          <div className="border-t border-[#ECECEC]" />
          <div className="bg-[#fff] py-4 rounded-b-[24px]">
            {listPending || isFetching ? (
              <TableSkeleton
                columns={[
                  t("S/N"),
                  t("DRS ID"),
                  t("Driver Name"),
                  t("Driver Code"),
                  t("Date & Time"),
                  t("Pending COD"),
                  t("Driver Wallet"),
                  t("COD Status"),
                  t("Action"),
                ]}
                rows={5}
              />
            ) : activeCodDrsList.length ? (
              <>
                <div className="w-full overflow-x-auto pb-4 custom-scrollbar">
                  <div
                    className={`flex flex-col gap-2 ${selectedDrs ? "min-w-[1150px]" : "md:min-w-[1150px] min-w-[800px]"}`}
                  >
                    <div className="flex items-center gap-4 px-6 pb-2 text-xs font-bold text-[#212121] uppercase border-b border-[#ECECEC] select-none">
                      <div className="w-10 shrink-0 text-center">{t("S/N")}</div>
                      <div className="flex-[0.8] min-w-[70px] text-left">
                        {t("DRS ID")}
                      </div>
                      <div className="flex-[0.9] min-w-[90px] text-left">
                        {t("Driver")}
                      </div>
                      <div className="hidden md:block flex-[0.8] min-w-[80px] text-left">
                        {t("Driver Code")}
                      </div>
                      <div className="hidden md:block flex-[0.8] min-w-[80px] text-left">
                        {t("Route Code")}
                      </div>
                      <div className="hidden lg:block flex-[1] min-w-[90px] text-center">
                        {t("Date")}
                      </div>
                      <div className="flex-[0.9] min-w-[100px] text-center">
                        {t("Pending COD")}
                      </div>
                      <div className="flex-[0.9] min-w-[95px] text-center">
                        {t("Driver Wallet")}
                      </div>
                      <div className="flex-[0.8] min-w-[85px] text-center">
                        {t("Status")}
                      </div>
                      <div className="flex-[0.7] min-w-[80px] text-center">
                        {t("Action")}
                      </div>
                    </div>

                    {activeCodDrsList.map((item: any, idx: number) => {
                      const reconCheck = getReconciliationCheck(item);
                      const codStatus = getCodStatus(item);
                      const isSelected = selectedDrs?.id === item.id;

                      return (
                        <div
                          key={item.id || idx}
                          onClick={() => setSelectedDrs(item)}
                          className={`bg-white border rounded-lg shadow-sm hover:shadow-md hover:bg-gray-50/50 transition-all cursor-pointer flex items-center gap-4 px-6 py-4 text-sm text-[#212121]
                          ${isSelected ? "border-2 border-[#FBD037] bg-[#FFFDE7]/40" : "border-[#ECECEC]"}`}
                        >
                          {/* S/N */}
                          <div className="w-10 shrink-0 text-center text-xs font-medium text-slate-400">
                            {idx + 1}
                          </div>

                          {/* DRS ID */}
                          <div className="flex-[0.8] min-w-[70px]  text-[#212121]">
                            {item.drsId}
                          </div>

                          {/* Driver */}
                          <div className="flex-[0.9] min-w-[90px] flex flex-col justify-center">
                            <span className=" text-[#212121]">
                              {item.driver}
                            </span>
                          </div>

                          {/* Driver Code */}
                          <div className="hidden md:block flex-[0.8] min-w-[80px] text-left font-normal text-[#212121]/70">
                            {item.driverCode}
                          </div>
                          <div className="hidden md:block flex-[0.8] min-w-[80px] text-left font-normal text-[#212121]/70">
                            {item.routeCode}
                          </div>

                          {/* Date & Time */}
                          <div className="hidden lg:block flex-[1] min-w-[90px] text-center font-medium text-slate-500 leading-tight text-[11px]">
                            {formatDate(item.createdAt)}
                          </div>

                          {/*  COD */}
                          <div className="flex-[0.9] min-w-[100px] text-center font-normal text-[#212121]/70">
                            SAR {item.pendingAmount || 0}
                          </div>

                          {/* Driver Wallet */}
                          <div className="flex-[0.9] min-w-[95px] text-center font-normal text-blue-600">
                            SAR {item.driverWallet || 0}
                          </div>

                          {/* COD Status */}
                          <div className="flex-[0.8] min-w-[85px] text-center">
                            <span
                              className={`inline-block px-2.5 py-1 rounded-md text-[10px] font-bold uppercase
                            ${
                              codStatus === "Collected"
                                ? "bg-green-50 text-green-700 border border-green-300"
                                : codStatus === "Closed"
                                  ? "bg-slate-100 text-slate-700 border border-slate-300"
                                  : "bg-yellow-50 text-yellow-800 border border-yellow-300"
                            }`}
                            >
                              {t(codStatus)}
                            </span>
                          </div>

                          {/* Action */}
                          <div className="flex-[0.7] min-w-[80px] text-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedDrs(item);
                              }}
                              className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all shadow-sm
                              ${
                                isSelected
                                  ? "bg-[#FBD037] text-[#212121] border border-[#FBD037] hover:bg-[#eab308]"
                                  : "bg-[#212121] hover:bg-[#333] text-white"
                              }`}
                            >
                              {isSelected ? t("Selected") : t("Select DRS")}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex justify-center items-center py-4">
                  {codOrders?.totalPages > 1 && (
                    <Pagination
                      currentPage={pageNo}
                      totalPages={codOrders.totalPages}
                      fetchData={handlePaginationFetch}
                    />
                  )}
                </div>
              </>
            ) : (
              <NoDataFound />
            )}
          </div>
        </div>
      </div>
      {selectedDrs && (
        <div className="col-span-12 xl:col-span-5 space-y-4 xl:static fixed inset-y-0 right-0 z-[35] w-full sm:w-[450px] xl:w-auto p-4 pt-[76px] sm:pt-[96px] xl:p-0 shadow-2xl xl:shadow-none bg-white xl:bg-transparent overflow-y-auto xl:overflow-visible flex flex-col justify-start">
          <CODSettlementDesk
            key={selectedDrs.id}
            selectedDrs={selectedDrs}
            refetchList={refetch}
            onClose={() => setSelectedDrs(null)}
          />
        </div>
      )}
    </div>
  );
};

export default CODSettlementView;
